import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { apiService } from '../../services/api';
import ChatBubble from './ChatBubble';
import ChatInput from './ChatInput';
import MuseAvatar from './MuseAvatar';
import { readSseEvents } from './sse';

// Conversation directe avec Muse, le modèle local du Mac mini : pas d'outils, pas de données
// Deepflow. L'historique vit dans le navigateur et part en entier à chaque message.

const MUSE_STATUS_POLL_MS = 30000;

const createGreeting = () => ({
  role: 'assistant',
  agent: 'muse',
  content: 'Muse, en direct du Mac mini. Je tourne en local : rien ne sort de chez Innowide.',
  greeting: true,
});

// État de Muse vu par le backend : unknown, online, offline ou unconfigured.
export const useMuseStatus = (active) => {
  const [status, setStatus] = useState('unknown');

  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;
    const check = async () => {
      try {
        const data = await apiService.get('/muse/status');
        if (cancelled) return;
        if (!data?.configured) setStatus('unconfigured');
        else setStatus(data.online ? 'online' : 'offline');
      } catch {
        if (!cancelled) setStatus('offline');
      }
    };
    check();
    const interval = setInterval(check, MUSE_STATUS_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [active]);

  return status;
};

// Tenu par ChatBot pour survivre à la fermeture de la fenêtre et au passage par Nexus.
export const useMuseConversation = () => {
  const [messages, setMessages] = useState(() => [createGreeting()]);
  const [pending, setPending] = useState(null); // { reasoning, content } pendant la réponse
  const abortRef = useRef(null);
  const messagesRef = useRef(messages);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const send = useCallback(async (rawMessage) => {
    const text = rawMessage.trim();
    if (!text || abortRef.current) return;

    const userMessage = { role: 'user', content: text };
    const history = [...messagesRef.current, userMessage]
      .filter((message) => !message.greeting && !message.isError)
      .map(({ role, content }) => ({ role, content }));
    setMessages((prev) => [...prev, userMessage]);
    setPending({ reasoning: '', content: '' });

    const controller = new AbortController();
    abortRef.current = controller;
    let reasoning = '';
    let content = '';
    let error = null;

    try {
      const response = await fetch(`${apiService.getBaseUrl()}/muse/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...apiService.getHeaders() },
        body: JSON.stringify({ messages: history }),
        signal: controller.signal,
      });
      if (!response.ok || !response.body) {
        throw new Error(`HTTP ${response.status}`);
      }
      await readSseEvents(response.body.getReader(), (event) => {
        if (event.event === 'reasoning') reasoning += event.data?.content || '';
        else if (event.event === 'delta') content += event.data?.content || '';
        else if (event.event === 'error') error = event.data?.message || 'Muse a rencontré une erreur.';
        setPending({ reasoning, content });
      });
    } catch (err) {
      // Conversation effacée pendant la réponse : on n'ajoute rien.
      if (err?.name === 'AbortError') return;
      console.error('Erreur streaming Muse:', err);
      error = 'Impossible de joindre Muse. Réessaie dans un instant.';
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
    }

    const failure = error || (!content && 'Muse n’a rien répondu.');
    setPending(null);
    setMessages((prev) => [
      ...prev,
      ...(content ? [{ role: 'assistant', agent: 'muse', content, reasoning }] : []),
      ...(failure ? [{ role: 'assistant', agent: 'muse', content: failure, isError: true }] : []),
    ]);
  }, []);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setPending(null);
    setMessages([createGreeting()]);
  }, []);

  return { messages, pending, send, reset };
};

export const splitReasoningLines = (text) =>
  text.split('\n').map((line) => line.trim()).filter(Boolean);

// Une ligne trop longue reste sur une ligne et laisse voir sa fin, là où Muse écrit.
const ReasoningLine = ({ text, className }) => (
  <div className={`flex justify-end overflow-hidden whitespace-nowrap ${className}`}>
    <span className="min-w-full shrink-0">{text}</span>
  </div>
);

ReasoningLine.propTypes = {
  text: PropTypes.string.isRequired,
  className: PropTypes.string.isRequired,
};

// Le raisonnement défile ligne par ligne : la ligne en cours s'écrit, la précédente s'estompe
// au-dessus, et tout disparaît quand la réponse commence.
export const ReasoningTicker = ({ text }) => {
  const lines = splitReasoningLines(text);
  const current = lines[lines.length - 1] || '…';
  const previous = lines[lines.length - 2];

  return (
    <div className="flex items-start gap-3 animate-muse-line-in">
      <MuseAvatar thinking />
      <div className="min-w-0 flex-1 rounded-2xl border border-teal-100 bg-teal-50/60 px-4 py-2.5">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-teal-500">Muse réfléchit…</p>
        <div className="mt-1 space-y-0.5 text-[13px] leading-5">
          {previous && (
            <div key={`previous-${lines.length}`} className="animate-muse-line-out">
              <ReasoningLine text={previous} className="text-teal-900/35" />
            </div>
          )}
          <div key={`current-${lines.length}`} className="animate-muse-line-in">
            <ReasoningLine text={current} className="text-teal-900/80" />
          </div>
        </div>
      </div>
    </div>
  );
};

ReasoningTicker.propTypes = {
  text: PropTypes.string.isRequired,
};

const statusNotices = {
  offline: 'Muse ne répond pas : le Mac mini est peut-être éteint ou sa session fermée.',
  unconfigured: 'Muse n’est pas encore relié à ce serveur Deepflow.',
};

const MuseChat = ({ conversation, status = 'unknown' }) => {
  const { messages, pending, send } = conversation;
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, pending]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex-1 space-y-4 overflow-y-auto overscroll-contain px-4 py-4 text-[0.95rem] leading-relaxed">
        {statusNotices[status] && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-xs text-amber-800">
            {statusNotices[status]}
          </div>
        )}
        {messages.map((message, idx) => (
          <div key={`muse-${idx}`}>
            <ChatBubble message={message} />
            {message.reasoning && (
              <details className="ml-11 mt-1 text-xs text-slate-400">
                <summary className="cursor-pointer select-none hover:text-teal-600">Raisonnement</summary>
                <p className="mt-1 max-h-48 overflow-y-auto whitespace-pre-wrap rounded-xl bg-slate-50 p-3 text-slate-500">
                  {message.reasoning.trim()}
                </p>
              </details>
            )}
          </div>
        ))}
        {pending && !pending.content && <ReasoningTicker text={pending.reasoning} />}
        {pending?.content && <ChatBubble message={{ role: 'assistant', agent: 'muse', content: pending.content }} />}
        <div ref={endRef} />
      </div>
      <ChatInput
        onSendMessage={send}
        isLoading={Boolean(pending)}
        placeholder="Écris à Muse…"
        allowAttachments={false}
      />
    </div>
  );
};

MuseChat.propTypes = {
  conversation: PropTypes.shape({
    messages: PropTypes.arrayOf(PropTypes.object).isRequired,
    pending: PropTypes.shape({ reasoning: PropTypes.string, content: PropTypes.string }),
    send: PropTypes.func.isRequired,
  }).isRequired,
  status: PropTypes.oneOf(['unknown', 'online', 'offline', 'unconfigured']),
};

export default MuseChat;
